import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../../setup/constants";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getExternalContractDetails } from "../../../setup/api/services/contract";
import { IContractDetailsResponseData } from "../../../setup/api/services/contract/models";
import OutlinedButton from "../../../components/common/OutlinedButton";
import StatsDetails from "../statsDetails";
import PDFComponent from "../../../components/common/PDFComponent";
import Status from "../status";
import { ContractContext } from "../../../providers/contract";
import classNames from "classnames";
import MilestoneActions from "./milestoneActions";
import MainDetails from "./MainDetails";
import CashoutDetails from "./CashoutDetails";
import EscalationSection from "./EscalationSection";

const MilestoneDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { actions } = useContext(ContractContext);
  const { id } = useParams();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const [milestone, setMilestone] = useState<
    IContractDetailsResponseData | undefined
  >(undefined);

  const fetchMilestoneDetails = useCallback(
    async (id: string) => {
      try {
        const response = await getExternalContractDetails(id);
        setMilestone(response?.data);
      } catch (error) {
        navigate(ROUTES.CONTRACT_NOT_FOUND);
        console.error(error);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (id) fetchMilestoneDetails(id);
  }, [fetchMilestoneDetails, id]);

  const list = useMemo(() => {
    return [
      {
        id: 0,
        title: t("lbl_main_tab_title"),
      },
      {
        id: 1,

        title: t("lbl_transfer_tab_title"),
      },
    ];
  }, [t]);

  return (
    <div className={styles.contractContainer}>
      <OutlinedButton
        onClick={() => {
          navigate(
            ROUTES.CONTRACT_DETAILS?.replace(":id", milestone?.parentId || ""),
            { state: { from: location?.state?.from } }
          );
          actions.resetContractState();
        }}
        style={{
          width: "139px",
          minHeight: "37px",
          color: "black",
          borderColor: "#E2E5EE",
          textAlign: "center",
          whiteSpace: "nowrap",
          fontSize: "12px",
          lineHeight: "22px",
          fontWeight: 500,
        }}>
        → {t("lbl_go_back_to_parent")}
      </OutlinedButton>
      <div className={styles.headerContainer}>
        <div className={styles.headerBlock}>
          <h1 className={styles.title}>{milestone?.itemDetail?.title}</h1>
          <p className={styles.subtitle}>{`${t("lbl_deadline")} : ${
            milestone?.deadLine
              ? new Date(milestone?.deadLine).toLocaleDateString("ar-EG", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })
              : ""
          }`}</p>
        </div>
        <MilestoneActions milestone={milestone} />
      </div>

      <Status contract={milestone} />

      {!!milestone?.escalations?.find((e) => !e.resolved) && (
        <EscalationSection milestone={milestone} />
      )}

      <StatsDetails milestoneId={milestone?.id || ""} />

      {!!milestone?.cashOutAmountList && (
        <div className={styles.tabContainer}>
          {list?.map((listItem) => (
            <li
              onClick={() => setSelectedIndex(listItem?.id)}
              key={listItem?.title}
              className={classNames(
                styles.listItem,
                {
                  [styles.listItemSelected]: listItem?.id === selectedIndex,
                },
                { [styles.firstItem]: listItem?.id === 0 },
                {
                  [styles.lastItem]:
                    listItem?.id === list?.[list?.length - 1]?.id,
                }
              )}>
              {listItem?.title}
            </li>
          ))}
        </div>
      )}

      {selectedIndex === 0 && <MainDetails milestone={milestone} />}

      {selectedIndex === 1 && <CashoutDetails milestone={milestone} />}

      {(!!milestone?.acceptanceConditions ||
        !!milestone?.cancellationConditions) && (
        <div className={styles.terms_conditions}>
          <h4>{t("lbl_terms_conditions")}</h4>
          <div
            className={classNames(
              styles.terms_conditions_wrapper,
              styles.card
            )}>
            {!!milestone?.acceptanceConditions && (
              <div className={styles.terms_conditions_content}>
                <p className={styles.title}>{t("lbl_acceptance_conditions")}</p>
                <p className={styles.description}>
                  {milestone?.acceptanceConditions}
                </p>
              </div>
            )}
            {!!milestone?.cancellationConditions && (
              <div className={styles.terms_conditions_content}>
                <p className={styles.title}>
                  {t("lbl_cancellation_conditions")}
                </p>
                <p className={styles.description}>
                  {milestone?.cancellationConditions}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: "16px",
          marginTop: "28px",
          width: "100%",
        }}>
        {((milestone?.itemDetail?.attachments &&
          milestone?.itemDetail?.attachments?.length !== 0) ||
          (milestone?.itemDetail?.images &&
            milestone?.itemDetail?.images?.length !== 0)) && (
          <h2 style={{ fontSize: "18px", fontWeight: 600, lineHeight: "100%" }}>
            {t("lbl_attachments")}
          </h2>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: "8px",
          }}>
          {milestone?.itemDetail?.attachments?.map((attachment) => (
            <PDFComponent
              key={attachment}
              isHorizontalPDF
              fileUrl={attachment}
            />
          ))}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "12px",
            overflowX: "auto",
            width: "100%",
          }}>
          {milestone?.itemDetail?.images?.map((image) => (
            <img
              key={image}
              src={image}
              alt="image"
              style={{
                height: "276px",
                width: "398px",
                borderRadius: "10px",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MilestoneDetails;
